import { useLayoutEffect, useMemo, useState } from "react";
import { useThemeSlice } from "../../../components/ui/StatusBadge.jsx";
import { ModalShellHeader } from "../../../components/modal/ModalShellHeader.jsx";
import { buildModalShellTokens } from "../../../components/modal/modalShellTheme.js";
import { Ico } from "../../../components/icons/icons.jsx";
import { AssetTypeColumnEditor } from "./AssetTypeColumnEditor.jsx";
import { assetTypeFormToPayload, assetTypeToForm, validateAssetTypeForm } from "../assetTypeUtils.js";

const fieldLabel = (c) => ({
  display: "block",
  fontSize: 11,
  fontWeight: 700,
  color: c.sub,
  marginBottom: 6,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
});

const inputStyle = (c, hasError) => ({
  width: "100%",
  height: 38,
  padding: "0 12px",
  borderRadius: 8,
  border: `1px solid ${hasError ? "#EF4444" : c.cardBdr}`,
  background: c.card,
  color: c.text,
  fontSize: 13,
  outline: "none",
  boxSizing: "border-box",
});

const textareaStyle = (c, hasError) => ({
  ...inputStyle(c, hasError),
  height: "auto",
  minHeight: 88,
  padding: "10px 12px",
  resize: "vertical",
  lineHeight: 1.5,
  fontFamily: "inherit",
});

export function AssetTypeModal({ open, onClose, onSave, editing = null, saving = false }) {
  const { c, dark, accentHex: A } = useThemeSlice();
  const ap = useMemo(() => buildModalShellTokens(dark, c, A), [dark, c, A]);
  const [form, setForm] = useState({ name: "", version: 1, description: "", appMeta: [], assetMeta: [] });
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const isEdit = Boolean(editing);

  useLayoutEffect(() => {
    if (!open) return;
    setForm(assetTypeToForm(editing));
    setErrors({});
    setSubmitError(null);
  }, [open, editing]);

  if (!open) return null;

  const handleSubmit = async () => {
    const nextErrors = validateAssetTypeForm(form);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    setSubmitError(null);
    try {
      await onSave(assetTypeFormToPayload(form));
    } catch (err) {
      setSubmitError(err.message ?? "Failed to save asset type");
    }
  };

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 70, background: ap.overlay, backdropFilter: "blur(8px)" }} role="presentation" />
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="asset-type-modal-title"
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "min(560px, 96vw)",
          maxHeight: "min(90vh, 820px)",
          zIndex: 71,
          background: ap.cardGradient,
          border: `1px solid ${ap.cardBorder}`,
          borderRadius: 14,
          boxShadow: ap.cardShadowLg,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <ModalShellHeader
          title={isEdit ? "Edit asset type" : "Create asset type"}
          titleId="asset-type-modal-title"
          subtitle={isEdit ? editing.name : "Define a schema for assets on this account"}
          icon={Ico.model}
          onClose={onClose}
          closeLabel="Close asset type form"
          dark={dark}
          c={c}
          A={A}
          ap={ap}
        />
        <div style={{ padding: "16px 20px 20px", overflow: "auto" }}>
          <label style={{ display: "block", marginBottom: 14 }}>
            <span style={fieldLabel(c)}>Asset type name</span>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="e.g. 2W, 3W/4W"
              style={inputStyle(c, errors.name)}
            />
            {errors.name && <span style={{ display: "block", marginTop: 4, fontSize: 11, color: "#EF4444" }}>{errors.name}</span>}
          </label>

          <label style={{ display: "block", marginBottom: 14 }}>
            <span style={fieldLabel(c)}>Version</span>
            <input
              type="number"
              min={1}
              step={1}
              value={form.version}
              onChange={(e) => setForm((prev) => ({ ...prev, version: e.target.value }))}
              style={{ ...inputStyle(c, errors.version), width: 120 }}
            />
            {errors.version && <span style={{ display: "block", marginTop: 4, fontSize: 11, color: "#EF4444" }}>{errors.version}</span>}
          </label>

          <label style={{ display: "block", marginBottom: 18 }}>
            <span style={fieldLabel(c)}>Description</span>
            <textarea
              value={form.description}
              onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
              placeholder="Describe this asset type schema…"
              rows={3}
              style={textareaStyle(c, errors.description)}
            />
            {errors.description && <span style={{ display: "block", marginTop: 4, fontSize: 11, color: "#EF4444" }}>{errors.description}</span>}
          </label>

          <AssetTypeColumnEditor
            sectionTitle="App meta"
            columns={form.appMeta}
            errors={errors.appMeta ?? []}
            onChange={(appMeta) => setForm((prev) => ({ ...prev, appMeta }))}
            entityLabel="app meta section"
          />

          <AssetTypeColumnEditor
            sectionTitle="Asset meta"
            columns={form.assetMeta}
            errors={errors.assetMeta ?? []}
            onChange={(assetMeta) => setForm((prev) => ({ ...prev, assetMeta }))}
            entityLabel="asset meta section"
          />

          {submitError && (
            <p style={{ margin: "14px 0 0", fontSize: 12, color: "#EF4444", fontWeight: 600 }}>{submitError}</p>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 20 }}>
            <button type="button" onClick={onClose} style={{ height: 36, padding: "0 14px", borderRadius: 8, border: `1px solid ${c.cardBdr}`, background: c.card, color: c.text, fontWeight: 600, cursor: "pointer" }}>
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={saving}
              style={{ height: 36, padding: "0 16px", borderRadius: 8, border: "none", background: A, color: "#fff", fontWeight: 700, cursor: saving ? "wait" : "pointer", opacity: saving ? 0.7 : 1 }}
            >
              {saving ? "Saving…" : isEdit ? "Save changes" : "Create asset type"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
